import React from "react";

interface OnyxMascotProps {
  size?: "tiny" | "small" | "medium" | "large";
  animate?: boolean;
  className?: string;
}

const sizes = {
  tiny: { width: 32, height: 32 },
  small: { width: 48, height: 56 },
  medium: { width: 120, height: 140 },
  large: { width: 200, height: 240 },
};

export function OnyxMascot({ size = "medium", animate = true, className = "" }: OnyxMascotProps) {
  const { width, height } = sizes[size];

  if (size === "tiny") {
    return (
      <svg
        width={width}
        height={height}
        viewBox="0 0 48 48"
        className={`${animate ? "onyx-float" : ""} ${className}`}
      >
        <circle cx="24" cy="24" r="22" fill="url(#tinyBg)" stroke="#7b5ea7" strokeWidth="2" />
        <ellipse cx="17" cy="21" rx="5" ry="6" fill="#a078d0" />
        <ellipse cx="17" cy="21" rx="3.5" ry="4" fill="#d0b8f0" />
        <circle cx="18.5" cy="19.5" r="1.5" fill="white" opacity="0.8" />
        <ellipse cx="31" cy="21" rx="5" ry="6" fill="#a078d0" />
        <ellipse cx="31" cy="21" rx="3.5" ry="4" fill="#d0b8f0" />
        <circle cx="32.5" cy="19.5" r="1.5" fill="white" opacity="0.8" />
        <path d="M 17 31 Q 24 38 31 31" stroke="#a078d0" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <defs>
          <radialGradient id="tinyBg" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#3a2860" />
            <stop offset="100%" stopColor="#201540" />
          </radialGradient>
        </defs>
      </svg>
    );
  }

  if (size === "small") {
    return (
      <svg
        width={width}
        height={height}
        viewBox="0 0 48 56"
        className={`${animate ? "onyx-float" : ""} ${className}`}
      >
        <circle cx="24" cy="8" r="4" fill="#a078d0" className="onyx-glow-anim" />
        <line x1="24" y1="12" x2="24" y2="18" stroke="#c0a0e0" strokeWidth="2" strokeLinecap="round" />
        <rect x="4" y="18" width="40" height="30" rx="10" fill="url(#smallHead)" stroke="#7b5ea7" strokeWidth="1.5" />
        <rect x="10" y="23" width="28" height="18" rx="6" fill="#1a1230" stroke="#5a4080" strokeWidth="0.5" />
        <ellipse cx="18" cy="30" rx="4" ry="4.5" fill="#a078d0" />
        <ellipse cx="18" cy="30" rx="3" ry="3.5" fill="#d0b8f0" />
        <circle cx="19" cy="28.5" r="1.2" fill="white" opacity="0.8" />
        <ellipse cx="30" cy="30" rx="4" ry="4.5" fill="#a078d0" />
        <ellipse cx="30" cy="30" rx="3" ry="3.5" fill="#d0b8f0" />
        <circle cx="31" cy="28.5" r="1.2" fill="white" opacity="0.8" />
        <path d="M 19 38 Q 24 42 29 38" stroke="#a078d0" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <defs>
          <linearGradient id="smallHead" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a2860" />
            <stop offset="100%" stopColor="#241848" />
          </linearGradient>
        </defs>
      </svg>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 220 260"
      className={`${animate ? "onyx-float" : ""} ${className}`}
    >
      {/* Antenna */}
      <line x1="110" y1="30" x2="110" y2="55" stroke="#c0a0e0" strokeWidth="3" strokeLinecap="round" />
      <circle cx="110" cy="24" r="8" fill="#a078d0" className="onyx-glow-anim" />
      <circle cx="110" cy="24" r="4" fill="#e0d0f8" />

      {/* Head */}
      <rect x="45" y="55" width="130" height="110" rx="30" fill="url(#onyxHeadGrad)" stroke="#7b5ea7" strokeWidth="2" />

      {/* Face plate */}
      <rect x="60" y="70" width="100" height="75" rx="18" fill="#1a1230" stroke="#5a4080" strokeWidth="1" />

      {/* Eyes */}
      <g className="onyx-blink">
        <ellipse cx="85" cy="100" rx="14" ry="16" fill="#a078d0" />
        <ellipse cx="85" cy="100" rx="10" ry="12" fill="#d0b8f0" />
        <circle cx="88" cy="96" r="4" fill="white" opacity="0.8" />

        <ellipse cx="135" cy="100" rx="14" ry="16" fill="#a078d0" />
        <ellipse cx="135" cy="100" rx="10" ry="12" fill="#d0b8f0" />
        <circle cx="138" cy="96" r="4" fill="white" opacity="0.8" />
      </g>

      {/* Smile */}
      <path d="M 88 125 Q 110 140 132 125" stroke="#a078d0" strokeWidth="3" fill="none" strokeLinecap="round" />

      {/* Cheek blush */}
      <ellipse cx="70" cy="118" rx="10" ry="6" fill="#7b5ea7" opacity="0.3" />
      <ellipse cx="150" cy="118" rx="10" ry="6" fill="#7b5ea7" opacity="0.3" />

      {/* Body */}
      <rect x="55" y="170" width="110" height="65" rx="20" fill="url(#onyxBodyGrad)" stroke="#7b5ea7" strokeWidth="2" />

      {/* Chat bubble on chest */}
      <rect x="85" y="182" width="50" height="32" rx="8" fill="#a078d0" opacity="0.3" />
      <circle cx="100" cy="198" r="3" fill="#d0b8f0" />
      <circle cx="110" cy="198" r="3" fill="#d0b8f0" />
      <circle cx="120" cy="198" r="3" fill="#d0b8f0" />

      {/* Arms */}
      <ellipse cx="38" cy="195" rx="16" ry="20" fill="url(#onyxArmGrad)" stroke="#7b5ea7" strokeWidth="1.5" />
      <ellipse cx="182" cy="195" rx="16" ry="20" fill="url(#onyxArmGrad)" stroke="#7b5ea7" strokeWidth="1.5" />

      {/* Feet */}
      <ellipse cx="85" cy="242" rx="22" ry="12" fill="url(#onyxFootGrad)" stroke="#7b5ea7" strokeWidth="1.5" />
      <ellipse cx="135" cy="242" rx="22" ry="12" fill="url(#onyxFootGrad)" stroke="#7b5ea7" strokeWidth="1.5" />

      <defs>
        <linearGradient id="onyxHeadGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a2860" />
          <stop offset="100%" stopColor="#241848" />
        </linearGradient>
        <linearGradient id="onyxBodyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2e2050" />
          <stop offset="100%" stopColor="#1e1438" />
        </linearGradient>
        <linearGradient id="onyxArmGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#352558" />
          <stop offset="100%" stopColor="#251840" />
        </linearGradient>
        <linearGradient id="onyxFootGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#352558" />
          <stop offset="100%" stopColor="#201438" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function OnyxMascotFace({ size = 32, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" className={className}>
      <circle cx="24" cy="24" r="22" fill="url(#faceBg)" stroke="#7b5ea7" strokeWidth="2" />
      <ellipse cx="17" cy="21" rx="5" ry="6" fill="#a078d0" />
      <ellipse cx="17" cy="21" rx="3.5" ry="4" fill="#d0b8f0" />
      <circle cx="18.5" cy="19.5" r="1.5" fill="white" opacity="0.8" />
      <ellipse cx="31" cy="21" rx="5" ry="6" fill="#a078d0" />
      <ellipse cx="31" cy="21" rx="3.5" ry="4" fill="#d0b8f0" />
      <circle cx="32.5" cy="19.5" r="1.5" fill="white" opacity="0.8" />
      <path d="M 17 31 Q 24 38 31 31" stroke="#a078d0" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <defs>
        <radialGradient id="faceBg" cx="50%" cy="40%">
          <stop offset="0%" stopColor="#3a2860" />
          <stop offset="100%" stopColor="#201540" />
        </radialGradient>
      </defs>
    </svg>
  );
}
